import React, { useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import LayersIcon from '@mui/icons-material/Layers';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import useVoyagerStore from '../../stores/useVoyagerStore';

const POPOVER_WIDTH = 420;

export default function PerspectiveChip() {
  const perspectives = useVoyagerStore((s) => s.perspectivesList);
  const domains = useVoyagerStore((s) => s.landingAgentDomains);
  const selectedDomainId = useVoyagerStore((s) => s.selectedLandingAgentDomainId);
  const selectedId = useVoyagerStore((s) => s.selectedPerspectiveId);
  const setSelected = useVoyagerStore((s) => s.setSelectedPerspective);
  // a pick is the thread's (#767); picking one in another domain selects that domain with it
  const pick = useVoyagerStore((s) => s.pickPerspective);

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Without the domain layer (#464) the chip is the whole catalog, never gated. With it, every perspective is
  // listed grouped by domain (#767), the selected domain's group first.
  const domainsOn = useVoyagerStore((s) => s.features.domains);
  const selectedDomain = domainsOn ? domains.find((d) => d.id === selectedDomainId) : undefined;
  const selected = perspectives.find((p) => p.id === selectedId);

  const scoped = useMemo(() => {
    const byName = (a: typeof perspectives[number], b: typeof perspectives[number]) => (a.name || a.id).localeCompare(b.name || b.id);
    if (!domainsOn) return [...perspectives].sort(byName);
    const order = [selectedDomainId, ...domains.map((d) => d.id)];
    const rank = (p: typeof perspectives[number]) => { const i = order.indexOf(p.domain ?? null); return i < 0 ? order.length : i; };
    return [...perspectives].sort((a, b) => rank(a) - rank(b) || byName(a, b));
  }, [perspectives, domains, selectedDomainId, domainsOn]);
  const domainName = (id?: string | null) => domains.find((d) => d.id === id)?.name || 'Other';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter(
      (p) =>
        (p.name || p.id).toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q),
    );
  }, [scoped, query]);

  const open = Boolean(anchor);
  const label = selectedId === 'auto' ? 'Auto' : selected?.name || selected?.id || 'Select perspective';
  const breadcrumb = selectedDomain?.name
    ? `${selectedDomain.name} › ${label}`
    : label;

  return (
    <>
      <Tooltip
        title={breadcrumb}
        disableHoverListener={!selected}
      >
        <span>
          <Button
            size="small"
            variant="text"
            onClick={(e) => setAnchor(e.currentTarget)}
            startIcon={<LayersIcon fontSize="small" />}
            endIcon={<ExpandMoreIcon fontSize="small" />}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: 999,
              px: 1.25,
              py: 0.25,
              '&:hover': { bgcolor: 'action.hover' },
              border: '1px solid var(--line)', bgcolor: 'var(--surface)',
            }}
          >
            <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
              {label}
            </Typography>
          </Button>
        </span>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => { setAnchor(null); setQuery(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: POPOVER_WIDTH, mt: 0.5 } } }}
        onTransitionEnd={() => { if (open) searchRef.current?.focus(); }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            {domainsOn ? 'All perspectives, by domain' : 'All perspectives'}
          </Typography>
        </Box>
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            inputRef={searchRef}
            size="small"
            fullWidth
            placeholder="Search perspectives…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List dense disablePadding sx={{ maxHeight: 420, overflow: 'auto', py: 0.5 }}>
          {scoped.length > 0 && <ListItemButton selected={selectedId === 'auto'} onClick={() => {
            setSelected('auto'); setAnchor(null); setQuery('');
          }}>
            <Box><Typography variant="body2">Auto</Typography><Typography variant="caption" color="text.secondary">Let the planner choose for each question</Typography></Box>
          </ListItemButton>}
          {filtered.length === 0 && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                No perspectives match.
              </Typography>
            </Box>
          )}
          {filtered.map((p, i) => {
            const isSelected = p.id === selectedId;
            const header = domainsOn && (i === 0 || filtered[i - 1].domain !== p.domain)
              ? <ListSubheader key={`h-${p.domain ?? ''}`} data-testid="perspective-group" sx={{ lineHeight: 2.5 }}>{domainName(p.domain)}</ListSubheader> : null;
            return [header,
              <Tooltip
                key={p.id}
                title={p.description || ''}
                placement="right"
                enterDelay={400}
                disableHoverListener={!p.description}
              >
                <ListItemButton
                  selected={isSelected}
                  onClick={() => {
                    pick(p.id);
                    setAnchor(null);
                    setQuery('');
                  }}
                  sx={{
                    alignItems: 'flex-start',
                    borderLeft: 3,
                    borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                    '&.Mui-selected': { bgcolor: 'action.selected' },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: isSelected ? 600 : 500 }}
                      noWrap
                    >
                      {p.name || p.id}
                    </Typography>
                    {p.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {p.description}
                      </Typography>
                    )}
                  </Box>
                  {isSelected && (
                    <CheckIcon fontSize="small" color="primary" sx={{ ml: 1, mt: 0.25 }} />
                  )}
                </ListItemButton>
              </Tooltip>];
          })}
        </List>
      </Popover>
    </>
  );
}
